import {
  RaceId, ShipClass, RACE_INFO, ALL_RACE_IDS,
  formatRaceTraitSummary,
  isPremiumRace, canAccountUseRace, raceLabelColorCss,
  t, getLocale,
  raceNameKey, raceDescriptionKey,
} from '@viral-space/shared';
import {
  COLORS, GLOW, el, css,
  createScreenRoot, buildStarfield, createButton, createInput, createTitle,
  injectSharedKeyframes,
} from './ui-shared';
import { mountShipCardPreview, type ShipCardPreviewHandle } from './ship-card-preview';

export interface CharacterData {
  callsign: string;
  race: RaceId;
  /**
   * Always {@link ShipClass.Scout} for new pilots — every player launches
   * in a Scout and unlocks heavier hulls through the station Hangar. Kept
   * on the type so downstream consumers (`SavedCharacter`, save flow) keep
   * working without branching.
   */
  shipClass: ShipClass;
}

export interface CharCreateOptions {
  /**
   * Whether the calling account currently has an active Premium
   * subscription. Premium-only race cards (everything outside the free
   * roster of Terran / Zyr'kath / Luminara) are greyed out, marked with
   * a "Premium" badge, and refuse selection when this is false. Server
   * still re-checks on save, so a tampered local value can't unlock
   * gameplay — this flag only drives presentation.
   */
  hasPremium: boolean;
}

export class CharCreateScreen {
  private container: HTMLElement;
  private root: HTMLElement;
  private onComplete: (data: CharacterData) => void;
  private onBack: () => void;
  private hasPremium: boolean;

  private selectedRace: RaceId = RaceId.Terran;
  private raceCards: Map<RaceId, HTMLElement> = new Map();
  private callsignInput!: HTMLInputElement;
  private stepIndex = 0;
  private steps: HTMLElement[] = [];
  private racePreviewCanvas: HTMLCanvasElement | null = null;
  private racePreviewHandle: ShipCardPreviewHandle | null = null;
  /** Exit create flow (pilot list or login). Only used on the callsign step. */
  private flowBackBtn: HTMLElement | null = null;
  private premiumNotice: HTMLElement | null = null;

  constructor(
    container: HTMLElement,
    onComplete: (data: CharacterData) => void,
    onBack: () => void,
    options: CharCreateOptions = { hasPremium: false },
  ) {
    this.container = container;
    this.onComplete = onComplete;
    this.onBack = onBack;
    this.hasPremium = options.hasPremium;
    injectSharedKeyframes();
    injectCharCreateStyles();
    this.root = createScreenRoot(container);
    buildStarfield(this.root);
    this.buildContent();
  }

  /**
   * Update the premium snapshot (e.g. after a `/api/billing/status`
   * refresh) and re-render the race grid so newly-unlocked cards
   * become selectable without rebuilding the whole screen.
   */
  setPremium(hasPremium: boolean): void {
    if (this.hasPremium === hasPremium) return;
    this.hasPremium = hasPremium;
    this.refreshRaceCards();
  }

  private buildContent(): void {
    const content = el('div', {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '24px',
      maxHeight: '100vh',
      overflowY: 'auto',
      width: '100%',
      boxSizing: 'border-box',
      animation: 'vs-fade-in 0.5s ease-out',
    });
    content.classList.add('vs-char-create-content');
    this.root.appendChild(content);

    content.appendChild(
      createTitle(t('char.createTitle'), 'medium'),
    );

    const stepIndicator = el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '8px',
      rowGap: '4px',
      alignItems: 'center',
      fontSize: '10px',
      color: COLORS.dim,
      textAlign: 'center',
    });
    const stepLabels = [t('char.step1Name'), t('char.step2Race')];
    for (const label of stepLabels) {
      const dot = el('span', { letterSpacing: '1px' }, label);
      stepIndicator.appendChild(dot);
      if (label !== stepLabels[stepLabels.length - 1]) {
        stepIndicator.appendChild(el('span', { color: COLORS.dim }, '›'));
      }
    }
    content.appendChild(stepIndicator);

    // Step 1: Callsign
    const step1 = this.createStep();
    const callsignLabel = el('div', {
      fontSize: '12px',
      color: COLORS.dim,
      textShadow: GLOW,
      marginBottom: '8px',
    }, t('char.chooseCallsign'));
    step1.appendChild(callsignLabel);

    this.callsignInput = createInput('PILOT-001', 'text', 16);
    step1.appendChild(this.callsignInput);

    const nameHint = el('div', {
      fontSize: '9px',
      color: COLORS.dim,
      textTransform: 'none',
      marginTop: '4px',
    }, t('char.nameVisibleHint'));
    step1.appendChild(nameHint);

    const nextBtn1 = createButton(
      t('char.next'),
      () => this.goToStep(1),
    );
    step1.appendChild(nextBtn1);
    this.steps.push(step1);
    content.appendChild(step1);

    // Step 2: Race + Launch.
    // Every new pilot launches in a Scout. The hull upgrade path lives in
    // the station Hangar, so this is the final step of character creation.
    const step2 = this.createStep();
    const raceLabel = el('div', {
      fontSize: '12px',
      color: COLORS.dim,
      textShadow: GLOW,
      marginBottom: '4px',
    }, t('char.selectRace'));
    step2.appendChild(raceLabel);

    // Premium status banner — gives non-premium pilots a heads-up that
    // some races are gated, and tells premium pilots their upgrades are
    // active. Kept short so it doesn't push the race grid off-screen on
    // mobile.
    this.premiumNotice = el('div', {
      fontSize: '10px',
      letterSpacing: '1px',
      textAlign: 'center',
      lineHeight: '1.4',
      maxWidth: '720px',
      padding: '6px 10px',
      borderRadius: '2px',
      textTransform: 'none',
    });
    this.premiumNotice.classList.add('vs-char-create-premium-notice');
    this.applyPremiumNotice();
    step2.appendChild(this.premiumNotice);

    const raceLayout = el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      width: '100%',
      maxWidth: '920px',
      gap: '16px',
      alignItems: 'flex-start',
      justifyContent: 'center',
      boxSizing: 'border-box',
    });
    const racePreviewWrap = el('div', {
      flex: '1 1 220px',
      minWidth: '0',
      maxWidth: '100%',
    });
    this.racePreviewCanvas = document.createElement('canvas');
    this.racePreviewCanvas.className = 'vs-char-create-race-preview-canvas';
    css(this.racePreviewCanvas, {
      width: '100%',
      aspectRatio: '4 / 3',
      display: 'block',
      background:
        'radial-gradient(circle at 50% 55%, rgba(0,40,30,0.45) 0%, rgba(0,8,0,0) 70%)',
      borderRadius: '2px',
    });
    const racePreviewHint = el('div', {
      fontSize: '9px',
      color: COLORS.dim,
      textAlign: 'center',
      textTransform: 'none',
      marginTop: '4px',
    }, t('char.raceStepPreviewHint'));
    racePreviewWrap.appendChild(this.racePreviewCanvas);
    racePreviewWrap.appendChild(racePreviewHint);
    raceLayout.appendChild(racePreviewWrap);

    const raceGrid = el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '10px',
      width: '100%',
      flex: '2 1 280px',
      minWidth: '0',
      maxWidth: '100%',
      pointerEvents: 'auto',
    });
    raceGrid.classList.add('vs-char-create-race-grid');

    for (const id of ALL_RACE_IDS) {
      const card = this.buildRaceCard(id);
      this.raceCards.set(id, card);
      raceGrid.appendChild(card);
    }
    raceLayout.appendChild(raceGrid);
    step2.appendChild(raceLayout);

    // Make the Scout-starter contract explicit so players don't expect a
    // ship-picker on the next screen.
    const starterNote = el('div', {
      fontSize: '10px',
      color: COLORS.secondary,
      textShadow: `0 0 4px ${COLORS.secondary}`,
      textAlign: 'center',
      maxWidth: '720px',
      textTransform: 'none',
      lineHeight: '1.4',
    }, t('char.starterScoutNote'));
    step2.appendChild(starterNote);

    const step2Btns = el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '12px',
      width: '100%',
    });
    step2Btns.appendChild(createButton(
      t('char.back'),
      () => this.goToStep(0),
      'ghost',
    ));
    step2Btns.appendChild(createButton(
      t('char.launch'),
      () => this.handleLaunch(),
    ));
    step2.appendChild(step2Btns);
    this.steps.push(step2);
    content.appendChild(step2);

    this.flowBackBtn = createButton(
      t('char.back'),
      () => this.onBack(),
      'ghost',
    );
    css(this.flowBackBtn, { marginTop: '8px', opacity: '0.5', fontSize: '10px' });
    content.appendChild(this.flowBackBtn);

    this.selectRace(this.selectedRace);
    this.goToStep(0);
  }

  /**
   * Build a single race card. Locked premium races are greyed out,
   * non-clickable, and surface a "Premium" badge instead of the
   * deprecated `UNLOCK LVL` legend so the gating model is obvious.
   */
  private buildRaceCard(id: RaceId): HTMLElement {
    const info = RACE_INFO[id];
    const locale = getLocale();
    const name = t(raceNameKey(id));
    const desc = t(raceDescriptionKey(id));
    const labelColor = raceLabelColorCss(id);
    const isLocked = !canAccountUseRace(id, this.hasPremium);

    const card = el('div', {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '12px',
      background: 'rgba(0,8,0,0.7)',
      border: `1px solid ${COLORS.dim}`,
      cursor: isLocked ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      pointerEvents: 'auto',
      touchAction: 'manipulation',
      opacity: isLocked ? '0.55' : '1',
      filter: isLocked ? 'saturate(0.7)' : 'none',
    });
    card.classList.add('vs-char-create-race-card');
    if (isLocked) card.classList.add('vs-char-create-race-card-locked');
    card.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    card.dataset.raceId = String(id);

    if (isPremiumRace(id)) {
      const badge = el('div', {
        position: 'absolute',
        top: '6px',
        right: '6px',
        padding: '2px 6px',
        background: isLocked ? 'rgba(255,176,0,0.18)' : 'rgba(255,176,0,0.85)',
        color: isLocked ? COLORS.secondary : '#000',
        fontSize: '8px',
        letterSpacing: '1px',
        borderRadius: '2px',
        border: `1px solid ${COLORS.secondary}`,
        boxShadow: `0 0 6px rgba(255,176,0,0.45)`,
        textShadow: 'none',
        pointerEvents: 'none',
      }, isLocked
        ? t('char.premiumLocked')
        : t('char.premiumUnlocked'));
      badge.classList.add('vs-char-create-race-badge');
      card.appendChild(badge);
    }

    card.appendChild(el('div', {
      fontSize: '13px',
      color: labelColor,
      textShadow: `0 0 6px ${labelColor}`,
      letterSpacing: '1px',
      paddingRight: isPremiumRace(id) ? '64px' : '0',
    }, name));

    card.appendChild(el('div', {
      fontSize: '9px',
      color: COLORS.dimText,
      lineHeight: '1.4',
      textTransform: 'none',
    }, desc));

    // Show the full balanced trait list rather than a single "+X% X"
    // label. The underlying RACE_TRAITS table is the single source of
    // truth for the numbers shown here.
    const traitLines = formatRaceTraitSummary(id, locale === 'es' ? 'es' : 'en');
    const traitsBox = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginTop: '4px',
    });
    if (traitLines.length === 0) {
      traitsBox.appendChild(el('div', {
        fontSize: '10px',
        color: COLORS.secondary,
        textShadow: `0 0 4px ${COLORS.secondary}`,
        textTransform: 'none',
      }, t('char.baselineStats')));
    } else {
      for (const line of traitLines) {
        traitsBox.appendChild(el('div', {
          fontSize: '10px',
          color: COLORS.secondary,
          textShadow: `0 0 4px ${COLORS.secondary}`,
          textTransform: 'none',
          lineHeight: '1.3',
        }, line));
      }
    }
    card.appendChild(traitsBox);

    // Surface the deep-sector spawn level only for races that historically
    // tied availability to player level. Now purely flavour ("appears at
    // level X+ in deep sectors") so picking the race doesn't suggest the
    // pilot itself is locked.
    if (info.unlockLevel > 0) {
      card.appendChild(el('div', {
        fontSize: '8px',
        color: COLORS.dim,
        marginTop: '2px',
        letterSpacing: '1px',
      }, t('char.deepSpaceTier', { level: info.unlockLevel })));
    }

    if (!isLocked) {
      card.addEventListener('click', () => this.selectRace(id));
    } else {
      // Tapping a locked card flashes the premium notice so mobile
      // users without hover get an immediate cue why nothing happened.
      card.addEventListener('click', () => this.flashPremiumNotice());
    }

    return card;
  }

  private refreshRaceCards(): void {
    this.applyPremiumNotice();
    for (const [id, oldCard] of this.raceCards) {
      const fresh = this.buildRaceCard(id);
      oldCard.replaceWith(fresh);
      this.raceCards.set(id, fresh);
    }
    // If the previously selected race just became locked, fall back to
    // the safest default so the launch button can't trigger a
    // server-side rejection.
    if (!canAccountUseRace(this.selectedRace, this.hasPremium)) {
      this.selectedRace = RaceId.Terran;
    }
    this.selectRace(this.selectedRace);
  }

  private applyPremiumNotice(): void {
    if (!this.premiumNotice) return;
    this.premiumNotice.style.background = this.hasPremium
      ? 'rgba(255,176,0,0.10)'
      : 'rgba(0,255,65,0.05)';
    this.premiumNotice.style.border = this.hasPremium
      ? `1px solid ${COLORS.secondary}`
      : `1px solid ${COLORS.dim}`;
    this.premiumNotice.style.color = this.hasPremium
      ? COLORS.secondary
      : COLORS.dimText;
    this.premiumNotice.textContent = this.hasPremium
      ? t('char.premiumActiveBanner')
      : t('char.premiumLockBanner');
  }

  private flashPremiumNotice(): void {
    if (!this.premiumNotice) return;
    this.premiumNotice.animate(
      [
        { borderColor: COLORS.secondary, boxShadow: `0 0 14px rgba(255,176,0,0.55)` },
        { borderColor: COLORS.dim, boxShadow: 'none' },
      ],
      { duration: 500, easing: 'ease-out' },
    );
  }

  private createStep(): HTMLElement {
    return el('div', {
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      animation: 'vs-fade-in 0.3s ease-out',
    });
  }

  private goToStep(idx: number): void {
    if (idx === 1 && !this.callsignInput.value.trim()) {
      this.callsignInput.style.borderColor = COLORS.danger;
      this.callsignInput.style.boxShadow = `0 0 8px ${COLORS.danger}`;
      this.callsignInput.placeholder = t('char.required');
      setTimeout(() => {
        this.callsignInput.style.borderColor = COLORS.primary;
        this.callsignInput.style.boxShadow = `0 0 8px rgba(0,255,65,0.2), inset 0 0 8px rgba(0,255,65,0.05)`;
      }, 1000);
      return;
    }

    this.stepIndex = idx;
    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i].style.display = i === idx ? 'flex' : 'none';
    }

    if (this.flowBackBtn) {
      this.flowBackBtn.style.display = idx === 0 ? '' : 'none';
    }

    if (idx === 1) {
      this.ensureRacePreview();
      this.racePreviewHandle?.setRotating(true);
      this.syncRacePreviewShip();
    } else {
      this.racePreviewHandle?.setRotating(false);
    }
  }

  private selectRace(id: RaceId): void {
    if (!canAccountUseRace(id, this.hasPremium)) {
      this.flashPremiumNotice();
      return;
    }
    this.selectedRace = id;
    for (const [rId, card] of this.raceCards) {
      if (rId === id) {
        card.style.border = `1px solid ${COLORS.primary}`;
        card.style.boxShadow = `0 0 12px rgba(0,255,65,0.3), inset 0 0 12px rgba(0,255,65,0.05)`;
      } else {
        card.style.border = `1px solid ${COLORS.dim}`;
        card.style.boxShadow = 'none';
      }
    }
    this.syncRacePreviewShip();
  }

  private handleLaunch(): void {
    if (!canAccountUseRace(this.selectedRace, this.hasPremium)) {
      this.flashPremiumNotice();
      return;
    }
    this.onComplete({
      callsign: this.callsignInput.value.trim(),
      race: this.selectedRace,
      shipClass: ShipClass.Scout,
    });
  }

  private currentRaceLivery(): { primaryColor: number; secondaryColor: number } {
    const info = RACE_INFO[this.selectedRace];
    return { primaryColor: info.primaryColor, secondaryColor: info.secondaryColor };
  }

  private ensureRacePreview(): void {
    if (this.racePreviewHandle || !this.racePreviewCanvas) return;
    this.racePreviewHandle = mountShipCardPreview(this.racePreviewCanvas, {
      shipClass: ShipClass.Scout,
      ...this.currentRaceLivery(),
      rotating: true,
    });
  }

  private syncRacePreviewShip(): void {
    if (!this.racePreviewHandle) return;
    this.racePreviewHandle.setShip(ShipClass.Scout, this.currentRaceLivery());
  }

  show(): void {
    this.root.style.display = 'flex';
    this.goToStep(0);
    setTimeout(() => this.callsignInput?.focus(), 100);
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  /** Release WebGL contexts before the game HUD or a new create flow mounts. */
  dispose(): void {
    this.racePreviewHandle?.dispose();
    this.racePreviewHandle = null;
    this.root.remove();
  }
}

let charCreateStylesInjected = false;

/**
 * One-shot CSS injection for the char-create screen. Keeps the responsive
 * grid + premium badge sizing rules out of the inline-styles soup so the
 * media queries can actually narrow the cards on phones (inline styles
 * win against shared `vs-char-create-content` rules already declared in
 * `ui-shared.injectSharedKeyframes`).
 */
function injectCharCreateStyles(): void {
  if (charCreateStylesInjected) return;
  charCreateStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .vs-char-create-race-grid {
      grid-auto-rows: 1fr;
    }
    .vs-char-create-race-card-locked .vs-char-create-race-badge {
      box-shadow: none;
    }

    /* Phones: tighten everything so the whole grid fits in one viewport.
       Desktop sizing is left untouched (the media query never matches). */
    @media (max-width: 640px) {
      .vs-char-create-race-grid {
        grid-template-columns: repeat(auto-fill, minmax(132px, 1fr)) !important;
        gap: 8px !important;
      }
      .vs-char-create-race-card {
        padding: 10px 8px !important;
        gap: 4px !important;
      }
      .vs-char-create-race-card > div:first-of-type {
        font-size: 12px !important;
      }
      .vs-char-create-premium-notice {
        font-size: 9px !important;
        padding: 5px 8px !important;
      }
    }

    /* Even narrower phones: drop to two compact columns and shrink type. */
    @media (max-width: 380px) {
      .vs-char-create-race-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 6px !important;
      }
      .vs-char-create-race-card {
        padding: 8px 6px !important;
      }
    }

    /* Landscape phones: keep the layout flat — no point in a 4:3 preview
       eating two thirds of the height when the user's primary task is
       picking a card. */
    @media (max-height: 500px) and (orientation: landscape) {
      .vs-char-create-race-preview-canvas {
        max-height: clamp(96px, 30vh, 160px) !important;
      }
      .vs-char-create-race-grid {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important;
      }
    }
  `;
  document.head.appendChild(style);
}
